import {
  type Facility,
  RXDB_FACILITY_COLLECTION_NAME,
} from "$global-providers/rxdb-provider/collections/facility";
import {
  RXDB_INVOICE_COLLECTION_NAME,
  type Invoice as RxInvoice,
} from "$global-providers/rxdb-provider/collections/invoice";
import {
  type Patient,
  RXDB_PATIENT_COLLECTION_NAME,
} from "$global-providers/rxdb-provider/collections/patient";
import { Invoice } from "$routes/_lib/billing-v2/invoice";
import type { PriceList } from "$routes/patient/[:patientId]/_lib/invoices/components/InvoiceCard/types";
import { gql } from "@apollo/client";
import { TransactionType } from "elephant-web/gql";
import { useRxData } from "rxdb-hooks";
import { usePrintableInvoiceCreatedByQuery } from "./__generated__/use-fetch-printable-invoice-rxdb.gql";
import { invoiceRecordFromDocument } from "./invoice-record-from-document";

// Mirrors the invoiceForReceipt Apollo query shape that the legacy printable
// components (LegacyPrintableInvoice, LegacyLaserReceiptPrint, printV2Receipt)
// consume. Keeping this shape stable means no changes are needed in those JSX
// components.
export type PrintableInvoiceDoc = {
  _id: string;
  invoiceNumber: string;
  patientType: string;
  created: string;
  invoiceStatus: string;
  balance: {
    paid: number;
    total: number;
    due: number;
    currency: string;
  };
  inpatientDetails: RxInvoice["inpatientDetails"];
  lineItems: Array<{
    id: string;
    priceListItem: { name: string; priceListItemType: string };
    quantity: number;
    cost: number;
    disabled: boolean;
  }>;
  patient: {
    cardId: string | null;
    passport: { uid: null };
    person: {
      name: { firstname: string; middlename: string | null; lastname: string };
      dob: string;
    };
    insurance: {
      insuranceType: string;
      insuranceDetails: unknown;
    } | null;
  };
  facility: {
    name: string;
    addresses: Facility["addresses"];
  };
  createdBy: {
    person: { firstname: string | null; lastname: string | null } | null;
    uuid: string;
  } | null;
  payers: Array<{
    type: string;
    name: string;
    balance: { paid: number; total: number };
    insuranceDetails: null;
  }>;
  transactions: Array<{
    type: string;
    payer: { type: string };
    payment: { paymentMethod: string | null; authCode: string | null };
    refund: { paymentMethod: string | null; authCode: string | null };
    amount: { value: number };
    actionedBy: null;
  }>;
};

// printV2Receipt filters insurer transactions with `t.payer.type !== "INSURER"`.
// SyncableTransaction.type is TransactionType; this maps it to the legacy payer-type slot.
function mapTransactionPayerType(txType: string): string {
  if (txType === TransactionType.ChargeInsurer) return "INSURER";
  if (txType === TransactionType.Waiver) return "WAIVER";
  if (txType === TransactionType.Exempt) return "EXEMPT";
  if (txType === TransactionType.Void) return "VOID";
  return "PATIENT";
}

export function mapInvoiceForPrint(
  doc: RxInvoice,
  patient: Patient | null,
  facility: Facility | null,
  createdByPerson: {
    firstname?: string | null;
    lastname?: string | null;
  } | null,
  createdByUserId: string,
  priceList: PriceList | null,
): PrintableInvoiceDoc {
  const invoice = Invoice.fromRecord(doc).toRecord();
  const priceListItemsById = new Map(
    (priceList?.priceListItems ?? []).map((item) => [item.uuid, item]),
  );

  return {
    _id: invoice.id,
    invoiceNumber: invoice.invoiceNumber,
    patientType: invoice.patientType,
    created: invoice.meta.createdAt,
    invoiceStatus: invoice.invoiceStatus,
    balance: {
      paid: invoice.balance.paid,
      total: invoice.balance.total,
      due: invoice.balance.due,
      currency: String(invoice.balance.currency),
    },
    inpatientDetails: invoice.inpatientDetails,
    lineItems: invoice.lineItems.map((item) => {
      const pli = item.priceListItemId
        ? priceListItemsById.get(item.priceListItemId)
        : undefined;
      return {
        id: item.id,
        // priceListItemType is not stored in the rxdb schema; the print
        // component renders "${priceListItemType} - ${name}" so an empty
        // string produces "- Name", which is the best we can do here.
        priceListItem: { name: pli?.name ?? "", priceListItemType: "" },
        quantity: item.quantity as number,
        cost: item.cost,
        disabled: item.disabled ?? false,
      };
    }),
    patient: patient
      ? {
          // nationalId.value is the closest rxdb equivalent to the legacy
          // cardId (hospital card number). passport.uid has no rxdb analogue.
          cardId: patient.nationalId?.value ?? null,
          passport: { uid: null },
          person: {
            name: {
              firstname: patient.name.first,
              middlename: patient.name.middle,
              lastname: patient.name.last,
            },
            dob: patient.dob,
          },
          insurance: patient.insurance
            ? {
                insuranceType: patient.insurance.type,
                insuranceDetails: patient.insurance.details,
              }
            : null,
        }
      : {
          cardId: null,
          passport: { uid: null },
          person: {
            name: { firstname: "", middlename: null, lastname: "" },
            dob: "",
          },
          insurance: null,
        },
    facility: facility
      ? { name: facility.name, addresses: facility.addresses }
      : { name: "", addresses: [] },
    createdBy: {
      person: createdByPerson
        ? {
            firstname: createdByPerson.firstname ?? null,
            lastname: createdByPerson.lastname ?? null,
          }
        : null,
      uuid: createdByUserId,
    },
    payers: invoice.payers.map((p) => ({
      type: p.type,
      name: p.name,
      balance: { paid: p.balance.paid, total: p.balance.total },
      insuranceDetails: null,
    })),
    transactions: invoice.transactions.map((t) => ({
      type: t.type,
      payer: { type: mapTransactionPayerType(t.type) },
      payment: {
        paymentMethod: t.paymentMethod ?? null,
        authCode: t.paymentAuthCode ?? null,
      },
      refund: {
        paymentMethod: t.refundPaymentMethod ?? null,
        authCode: t.refundAuthCode ?? null,
      },
      amount: { value: t.amount?.value ?? 0 },
      actionedBy: null,
    })),
  };
}

// One Apollo call kept for createdBy — api-user data lives outside the rxdb
// domain and there is no rxdb user collection today.
const PRINTABLE_INVOICE_CREATED_BY_DOCUMENT = gql`
  query PrintableInvoiceCreatedBy($userId: String!) {
    user(_id: $userId) {
      _id
      person {
        firstname
        lastname
      }
    }
  }
`;

export type FetchPrintableInvoiceRxdbResult = {
  invoice: PrintableInvoiceDoc | null;
  loading: boolean;
};

export function useFetchPrintableInvoiceRxdb(
  syncInvoiceId: string,
  priceList: PriceList | null,
): FetchPrintableInvoiceRxdbResult {
  const { result: invoiceResult, isFetching: invoiceFetching } =
    useRxData<RxInvoice>(RXDB_INVOICE_COLLECTION_NAME, (collection) =>
      collection.find({ selector: { id: syncInvoiceId } }),
    );

  const invoiceDoc = invoiceResult?.[0] ?? null;
  const patientId = invoiceDoc?.patientId ?? null;
  const facilityId = invoiceDoc?.facilityId ?? null;
  const createdByUserId = invoiceDoc?.meta?.createdByUserId ?? "";

  const { result: patientResult, isFetching: patientFetching } =
    useRxData<Patient>(RXDB_PATIENT_COLLECTION_NAME, (collection) =>
      patientId ? collection.find({ selector: { id: patientId } }) : null,
    );

  const { result: facilityResult, isFetching: facilityFetching } =
    useRxData<Facility>(RXDB_FACILITY_COLLECTION_NAME, (collection) =>
      facilityId ? collection.find({ selector: { id: facilityId } }) : null,
    );

  const { data: userData } = usePrintableInvoiceCreatedByQuery({
    variables: { userId: createdByUserId },
    skip: !createdByUserId,
  });

  const loading = invoiceFetching || patientFetching || facilityFetching;

  if (!invoiceDoc) {
    return { invoice: null, loading };
  }

  const patient = (patientResult?.[0] as Patient | undefined) ?? null;
  const facility = (facilityResult?.[0] as Facility | undefined) ?? null;
  const createdByPerson = userData?.user?.person ?? null;

  return {
    invoice: mapInvoiceForPrint(
      invoiceRecordFromDocument(invoiceDoc),
      patient,
      facility,
      createdByPerson,
      createdByUserId,
      priceList,
    ),
    loading,
  };
}
